import React, { useEffect, useState } from 'react';
import { Box, Checkbox, CircularProgress, ListItemText, MenuItem, Select, Typography } from '@mui/material';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import { useTranslation } from 'react-i18next';
import JobService from '../../services/JobService';
import UserService from '../../services/UserService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

export const initials = (u) => `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`.toUpperCase() || '?';

const Avatars = ({ people }) => (
    <Box sx={{ display: 'flex', flexShrink: 0 }}>
        {people.slice(0, 3).map((a, i) => (
            <Box key={a.id} sx={{
                width: 24, height: 24, borderRadius: '50%', bgcolor: '#427898', color: '#fff', border: '2px solid #fff',
                fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', ml: i ? -0.6 : 0,
            }}>{initials(a)}</Box>
        ))}
    </Box>
);

/**
 * Who works on the job, as a dropdown with checkboxes. Ticking does not save yet: the crew is saved once when
 * the menu closes, so picking two people is one request. Without `editable` it just shows the names.
 */
export default function AssigneeSelect({ job, editable, onChange }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [team, setTeam] = useState([]);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!editable) return;
        UserService.directory().then(setTeam).catch(() => {});
    }, [editable]);

    useEffect(() => { setSelected(job.assignees.map(a => a.id)); }, [job.assignees]);

    const names = (people) => people.map(a => `${a.firstName} ${a.lastName}`).join(', ');

    if (!editable) {
        return job.assignees.length ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Avatars people={job.assignees} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B' }}>{names(job.assignees)}</Typography>
            </Box>
        ) : <Typography sx={{ fontSize: 13.5, color: '#94A3B8' }}>{t('jobs.noAssignees')}</Typography>;
    }

    const close = async () => {
        setOpen(false);
        const before = job.assignees.map(a => a.id).sort().join(',');
        if ([...selected].sort().join(',') === before) return;
        setSaving(true);
        try {
            onChange(await JobService.setAssignees(job.id, selected));
            showToast(t('jobs.assign.saved'), 'success');
        } catch (err) {
            setSelected(job.assignees.map(a => a.id));
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setSaving(false);
        }
    };

    // The directory lists active people; someone deactivated stays visible while still assigned, so they can be removed.
    const options = [...team, ...job.assignees.filter(a => !team.some(u => u.id === a.id))];
    const byId = Object.fromEntries([...job.assignees, ...team].map(u => [u.id, u]));
    const chosen = selected.map(id => byId[id]).filter(Boolean);
    const empty = chosen.length === 0;

    return (
        <Select multiple size="small" value={selected} open={open} onOpen={() => setOpen(true)} onClose={close}
                onChange={(e) => setSelected(typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value)}
                displayEmpty disabled={saving} inputProps={{ 'aria-label': t('jobs.fields.assignees') }}
                renderValue={() => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        {saving ? <CircularProgress size={16} /> : empty
                            ? <PersonAddAlt1OutlinedIcon sx={{ fontSize: 18, color: '#B45309' }} />
                            : <Avatars people={chosen} />}
                        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: empty ? '#B45309' : '#1E293B' }}>
                            {empty ? t('jobs.noAssignees') : names(chosen)}
                        </Typography>
                    </Box>
                )}
                sx={{
                    minWidth: 200, maxWidth: { xs: '100%', sm: 360 }, borderRadius: '10px',
                    bgcolor: empty ? '#FFFBEB' : '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: empty ? '#FCD34D' : '#D5E3EC' },
                    '& .MuiSelect-select': { py: 0.6, display: 'flex', alignItems: 'center' },
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 360, minWidth: 240 } } }}>
            {options.map(u => (
                <MenuItem key={u.id} value={u.id} dense>
                    <Checkbox size="small" checked={selected.includes(u.id)} sx={{ p: 0.5, mr: 1 }} />
                    <ListItemText primary={`${u.firstName} ${u.lastName}`}
                                  secondary={u.role ? t(`team.roles.${u.role === 'ADMIN' ? 'ADMIN' : 'EMPLOYEE'}`) : t('team.status.INACTIVE')}
                                  primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }} secondaryTypographyProps={{ fontSize: 12 }} />
                </MenuItem>
            ))}
        </Select>
    );
}
